import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';
import { StatusBadge, PageHeader } from '../../ds';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'deleted', label: 'Deleted' }
];

// Fallback only — the dropdown is driven by summary.by_source_type (live GROUP BY
// from the API) so new enum members appear without a frontend change. Values must
// match journal_entries.source_type exactly; the API filters on equality.
const FALLBACK_SOURCE_TYPES = [
  'order_revenue', 'bank_transaction', 'amazon_fees', 'amazon_fees_refund', 'payout',
  'bill', 'manual', 'amazon_ads_expense', 'google_ads_expense', 'marketing_accrual',
  'ai_generated', 'intercompany_transfer', 'interbank_transfer'
];

const sourceTypeLabel = (v) =>
  v.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export default function JournalEntriesList({ summary, drill = null }) {
  // Live source-type list from the summary API (GROUP BY source_type), so the
  // filter never rots as the enum grows; static fallback if summary is absent
  const liveSourceTypes = summary?.by_source_type ? Object.keys(summary.by_source_type) : null;
  const sourceTypeOptions = [
    { value: 'all', label: 'All Sources' },
    ...((liveSourceTypes && liveSourceTypes.length > 0 ? liveSourceTypes : FALLBACK_SOURCE_TYPES)
      .slice()
      .sort()
      .map(v => ({ value: v, label: sourceTypeLabel(v) })))
  ];

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting
  const [orderBy, setOrderBy] = useState('entry_date');
  const [order, setOrder] = useState('desc');

  // Filtering
  // ?account_code=X&end_date=Y — Balance Sheet / Cash Flow drill-down: list the
  // live entries that make up that account's balance as of the report date.
  // Cash Flow also passes start_date (its report period).
  // `drill` ({ accountCode, startDate, endDate }) is the same filter passed as props, used when
  // the list opens inside the Balance Sheet / Cash Flow overlay instead of from the URL.
  const [filters, setFilters] = useState(() => {
    const qs = drill
      ? new URLSearchParams({ account_code: drill.accountCode || '', start_date: drill.startDate || '', end_date: drill.endDate || '' })
      : (typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search));
    const accountCode = qs.get('account_code') || '';
    return {
      status: accountCode ? 'active' : 'all',
      source_type: 'all',
      start_date: qs.get('start_date') || '',
      end_date: qs.get('end_date') || '',
      account_code: accountCode
    };
  });

  // Detail dialog
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [entryDetails, setEntryDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, [page, rowsPerPage, orderBy, order, filters]);

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sort: orderBy,
        order: order
      };

      if (filters.status !== 'all') params.status = filters.status;
      if (filters.source_type !== 'all') params.source_type = filters.source_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.account_code) params.account_code = filters.account_code;

      const response = await axios.get(`${API_URL}/api/accounting/journal-entries`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setEntries(response.data.data.entries);
        setTotalCount(response.data.data.pagination?.total ?? 0);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching journal entries:', err);
      setError(err.response?.data?.error?.message || 'Failed to load journal entries');
      setLoading(false);
    }
  };

  const fetchEntryDetails = async (entryId) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accounting/journal-entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setEntryDetails(response.data.data);
      }
      setLoadingDetails(false);
    } catch (err) {
      console.error('Error fetching entry details:', err);
      setLoadingDetails(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry);
    setDetailDialogOpen(true);
    fetchEntryDetails(entry.id);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedEntry(null);
    setEntryDetails(null);
  };

  // ?je_id=<id> — open that entry's detail dialog on mount. The dialog and its
  // loader already existed; without this a journal entry could not be linked to
  // from anywhere, which made every "GL >" affordance elsewhere a dead end.
  // Loads by id directly, so it works even when the entry is not on the current
  // page or inside the current filter window. Runs once — closing must not reopen.
  const jeDeepLinkOpened = React.useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || drill || jeDeepLinkOpened.current) return;
    const raw = new URLSearchParams(window.location.search).get('je_id');
    if (!raw || !/^\d+$/.test(raw)) return;
    jeDeepLinkOpened.current = true;
    // Minimal stand-in until the fetch returns: the dialog header reads
    // selectedEntry.entry_number, so show the id rather than a blank chip.
    setSelectedEntry({ id: raw, entry_number: `#${raw}` });
    setDetailDialogOpen(true);
    fetchEntryDetails(raw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entries store amounts in their own base_currency — never assume USD
  const formatCurrency = (value, currency) => {
    const num = parseFloat(value || 0);
    const plain = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // DT-1451(d): no currency means no symbol, never an assumed '$'. The label may wrap as one unit below the number.
    if (!currency) return `${plain} (currency unknown)`;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(num);
    } catch (e) {
      return `${currency} ${plain}`;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'superseded': return 'warning';
      case 'deleted': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <PageHeader
        primary="Journal Entries"
        metadata={[{ label: 'All journal entries posted by the reconciliation pipeline' }]}
      />
      {/* Summary Stats */}
      {summary && summary.by_status && Array.isArray(summary.by_status) && summary.by_status.length > 0 && (
        <Grid container spacing={2} mb={3}>
          {summary.by_status.map((stat, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="600">
                  {stat.count.toLocaleString()}
                </Typography>
                <Box sx={{ mt: 1 }}><StatusBadge status={stat.status} /></Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterListIcon color="action" />
          <Typography variant="subtitle2" fontWeight="600">
            Filters
          </Typography>
          {filters.account_code && (
            <Chip
              size="small"
              color="primary"
              label={`GL account ${filters.account_code}`}
              onDelete={() => handleFilterChange('account_code', '')}
            />
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              size="small"
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Source Type"
              value={filters.source_type}
              onChange={(e) => handleFilterChange('source_type', e.target.value)}
              size="small"
            >
              {sourceTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Entries Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'entry_number'}
                    direction={orderBy === 'entry_number' ? order : 'asc'}
                    onClick={() => handleSort('entry_number')}
                  >
                    Entry #
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'entry_date'}
                    direction={orderBy === 'entry_date' ? order : 'asc'}
                    onClick={() => handleSort('entry_date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_debits'}
                    direction={orderBy === 'total_debits' ? order : 'asc'}
                    onClick={() => handleSort('total_debits')}
                  >
                    Debits
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_credits'}
                    direction={orderBy === 'total_credits' ? order : 'asc'}
                    onClick={() => handleSort('total_credits')}
                  >
                    Credits
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No journal entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {entry.entry_number}
                      </Typography>
                      {entry.entry_version > 1 && (
                        <Chip
                          label={`v${entry.entry_version}`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(entry.entry_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.4
                        }}
                      >
                        {entry.description || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.source_type || 'Unknown'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        {formatCurrency(entry.total_debits, entry.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        {formatCurrency(entry.total_credits, entry.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={entry.status} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(entry)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Entry Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="600">
              Journal Entry Details
            </Typography>
            {selectedEntry && (
              <Chip
                label={selectedEntry.entry_number}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : entryDetails ? (
            <Box>
              {/* Entry Header Info */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Entry Date
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {formatDate(entryDetails.entry.entry_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <StatusBadge status={entryDetails.entry.status} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {entryDetails.entry.description || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Line Items */}
              <Typography variant="subtitle2" fontWeight="600" mb={2}>
                Line Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entryDetails.entry.lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {line.account_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {line.account_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {line.debit_amount > 0 ? formatCurrency(line.debit_amount, entryDetails.entry.currency) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {line.credit_amount > 0 ? formatCurrency(line.credit_amount, entryDetails.entry.currency) : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {line.description || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700">
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="700">
                          {formatCurrency(entryDetails.entry.total_debits, entryDetails.entry.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="700">
                          {formatCurrency(entryDetails.entry.total_credits, entryDetails.entry.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Version History */}
              {entryDetails.version_history && entryDetails.version_history.length > 1 && (
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight="600" mb={2}>
                    Version History
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Version</TableCell>
                          <TableCell>Entry #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entryDetails.version_history.map((version) => (
                          <TableRow
                            key={version.id}
                            sx={{
                              backgroundColor: version.is_superseded
                                ? 'rgba(0, 0, 0, 0.04)'
                                : 'transparent'
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={`v${version.entry_version}`}
                                size="small"
                                color={version.is_superseded ? 'default' : 'primary'}
                              />
                            </TableCell>
                            <TableCell>{version.entry_number}</TableCell>
                            <TableCell>{formatDate(version.entry_date)}</TableCell>
                            <TableCell>
                              {version.is_superseded ? (
                                <Chip label="Superseded" size="small" />
                              ) : (
                                <Chip label="Current" size="small" color="success" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">No details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
